import React, { useCallback, useEffect, useState } from 'react';

import { CircularProgress, LinearProgress, Typography } from '@mui/material';
import { Ban, CircleAlert, CircleCheck, X } from 'lucide-react';
import {
  BACKGROUND_TASK_ICON_SIZE,
  BackgroundTaskPanel,
  BackgroundTaskPanelAction,
} from 'src/components/background-tasks';
import { AlineButton, ConfirmationDialog } from 'src/components/generic';
import type { DocumentStatusJobPhase } from 'src/components/reports/report-detail/document-status-jobs';
import {
  getIsDocumentStatusJobActive,
  getIsDocumentStatusJobVisible,
} from 'src/components/reports/report-detail/document-status-jobs';
import {
  formatDocumentStatusJobDoneMessage,
  formatDocumentStatusJobProgress,
  formatDocumentStatusJobTitle,
} from 'src/components/reports/report-detail/grid-document-status';
import type { DocumentStatusJobView } from 'src/hooks/document-status-jobs';
import { useCancelDocumentStatusJob, useDocumentStatusJobs } from 'src/hooks/document-status-jobs';
import { makeStyles } from 'tss-react/mui';

type DocumentStatusJobProgress = DocumentStatusJobView['progress'];

const useStyles = makeStyles()(theme => ({
  detail: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    paddingLeft: theme.spacing(3),
  },
  iconDone: {
    color: theme.palette.success.main,
  },
  iconFailed: {
    color: theme.palette.error.main,
  },
  iconStopped: {
    color: theme.palette.text.secondary,
  },
  list: {
    maxHeight: 360,
    overflowY: 'auto',
  },
  progressBar: {
    '& .MuiLinearProgress-bar': {
      borderRadius: 999,
      transition: 'transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    borderRadius: 999,
    height: 4,
  },
  row: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.appColors.rule}`,
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 1, 1.25, 1.5),
  },
  rowHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 28,
    minWidth: 0,
  },
  rowIcon: {
    display: 'flex',
    flexShrink: 0,
  },
  rowTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    minWidth: 0,
  },
  secondaryText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: '18px',
  },
}));

const DocumentStatusJobRow = React.memo(function DocumentStatusJobRow({
  job,
  onDismiss,
}: {
  job: DocumentStatusJobView;
  onDismiss: (jobId: string) => void;
}): JSX.Element {
  const { classes } = useStyles();
  const { cancelJob } = useCancelDocumentStatusJob();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const { progress } = job;
  // A job whose batches have not reported yet has nothing to mark, so the request itself shows.
  const phase: DocumentStatusJobPhase =
    stopRequested && progress.phase === 'running' ? 'cancelling' : progress.phase;

  const handleStop = useCallback(async () => {
    setConfirmOpen(false);
    setStopRequested(true);
    try {
      await cancelJob(job.id);
    } catch {
      setStopRequested(false);
    }
  }, [cancelJob, job.id]);

  return (
    <div className={classes.row}>
      <div className={classes.rowHeader}>
        <span className={classes.rowIcon}>
          <DocumentStatusJobIcon numFailed={progress.numFailed} phase={phase} />
        </span>
        <Typography noWrap className={classes.rowTitle}>
          {formatDocumentStatusJobTitle({ ...progress, phase })}
        </Typography>
        <DocumentStatusJobRowAction
          phase={phase}
          onCancel={() => setConfirmOpen(true)}
          onDismiss={() => onDismiss(job.id)}
        />
      </div>
      <div className={classes.detail}>
        <DocumentStatusJobRowDetail phase={phase} progress={progress} />
      </div>
      <ConfirmationDialog
        message="Documents already updated keep their new status. The rest stay as they are."
        open={confirmOpen}
        primaryText="Yes, Cancel"
        secondaryText="Keep Going"
        title="Cancel this status change?"
        onClickPrimary={() => {
          void handleStop();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
});

/**
 * Every background status change this tab is showing, one row each, in a
 * single tray panel. A running job always shows; a finished one stays, with
 * its summary, until dismissed in a tab that watched it run.
 */
export const DocumentStatusJobsPanel = React.memo(
  function DocumentStatusJobsPanel(): JSX.Element | null {
    const { classes } = useStyles();
    const { jobs } = useDocumentStatusJobs();
    const [watchedIds, setWatchedIds] = useState<ReadonlySet<string>>(() => new Set());
    const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());

    useEffect(() => {
      const newlyActiveIds = jobs
        .filter(job => getIsDocumentStatusJobActive(job.progress.phase) && !watchedIds.has(job.id))
        .map(job => job.id);
      if (!newlyActiveIds.length) return;

      setWatchedIds(current => new Set([...current, ...newlyActiveIds]));
    }, [jobs, watchedIds]);

    const dismiss = useCallback((jobIds: string[]) => {
      setDismissedIds(current => new Set([...current, ...jobIds]));
    }, []);
    const handleDismissJob = useCallback((jobId: string) => dismiss([jobId]), [dismiss]);

    const visibleJobs = jobs.filter(job =>
      getIsDocumentStatusJobVisible({
        dismissed: dismissedIds.has(job.id),
        phase: job.progress.phase,
        watched: watchedIds.has(job.id),
      })
    );
    if (!visibleJobs.length) return null;

    const activeJobs = visibleJobs.filter(job => getIsDocumentStatusJobActive(job.progress.phase));

    return (
      <BackgroundTaskPanel
        collapsedHint={activeJobs.length > 0 && `${getPercent(sumProgress(activeJobs))}%`}
        count={visibleJobs.length}
        title="Document Status Updates"
        onClose={activeJobs.length ? undefined : () => dismiss(visibleJobs.map(job => job.id))}
      >
        <div className={classes.list}>
          {visibleJobs.map(job => (
            <DocumentStatusJobRow key={job.id} job={job} onDismiss={handleDismissJob} />
          ))}
        </div>
      </BackgroundTaskPanel>
    );
  }
);

function DocumentStatusJobRowAction({
  onCancel,
  onDismiss,
  phase,
}: {
  onCancel: () => void;
  onDismiss: () => void;
  phase: DocumentStatusJobPhase;
}): JSX.Element | null {
  if (phase === 'running')
    return (
      <AlineButton color="primary" size="small" variant="text" onClick={onCancel}>
        Cancel
      </AlineButton>
    );
  if (phase === 'cancelling') return null;

  return (
    <BackgroundTaskPanelAction label="Dismiss" onClick={onDismiss}>
      <X size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
    </BackgroundTaskPanelAction>
  );
}

function DocumentStatusJobRowDetail({
  phase,
  progress,
}: {
  phase: DocumentStatusJobPhase;
  progress: DocumentStatusJobProgress;
}): JSX.Element {
  const { classes } = useStyles();

  if (!getIsDocumentStatusJobActive(phase))
    return (
      <Typography className={classes.secondaryText}>
        {formatDocumentStatusJobDoneMessage(progress)}
      </Typography>
    );

  return (
    <>
      <LinearProgress
        className={classes.progressBar}
        value={getPercent(progress)}
        variant="determinate"
      />
      <Typography className={classes.secondaryText}>
        {phase === 'cancelling'
          ? 'Finishing the documents already in progress'
          : formatDocumentStatusJobProgress(progress)}
      </Typography>
    </>
  );
}

function DocumentStatusJobIcon({
  numFailed,
  phase,
}: {
  numFailed: number;
  phase: DocumentStatusJobPhase;
}): JSX.Element {
  const { classes } = useStyles();

  if (getIsDocumentStatusJobActive(phase))
    return <CircularProgress size={BACKGROUND_TASK_ICON_SIZE} thickness={5} />;
  if (phase === 'cancelled')
    return <Ban className={classes.iconStopped} size={BACKGROUND_TASK_ICON_SIZE} />;
  if (numFailed > 0)
    return <CircleAlert className={classes.iconFailed} size={BACKGROUND_TASK_ICON_SIZE} />;

  return <CircleCheck className={classes.iconDone} size={BACKGROUND_TASK_ICON_SIZE} />;
}

function sumProgress(jobs: DocumentStatusJobView[]): { numFinished: number; numQueued: number } {
  return jobs.reduce(
    (total, { progress }) => ({
      numFinished: total.numFinished + progress.numFinished,
      numQueued: total.numQueued + progress.numQueued,
    }),
    { numFinished: 0, numQueued: 0 }
  );
}

function getPercent(progress: { numFinished: number; numQueued: number }): number {
  if (!progress.numQueued) return 0;
  return Math.min(100, Math.round((progress.numFinished / progress.numQueued) * 100));
}
